import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import packagedGoodsControlsService from '../../service/api/PackagedGoodsControlsService';
import { Dialog } from 'primereact/dialog';
import PackagedGoodsControlDialog from './PackagedGoodsControlDialog';
import { Trans, useTranslation } from 'react-i18next';
import { PackagedGoodsControl, ControlType, Status, Result } from '../../types/PackagedGoodsControl';
import { TOAST_LIFETIME } from '../../settings';
import WriteOnlyButton from '../controls/WriteOnlyButton';
import enumService from '../../service/EnumService';

import getPaginatorTemplate from '../_shared/getPaginatorTemplate';
import { usePagination } from '../_shared/hooks/usePagination';
import { TableFilterData, SearchMode } from '../../types/general-types';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { ColumnDefinitions, useColumnFactory } from '../_shared/columnFactory';
import { generateFiltersFromColumns } from '../_shared/tableConfiguration';
import { getFileFromBuffer } from '../_shared/getFile';
import { format } from 'date-fns';
import { REPORT_TYPES } from '../../enums/report-types.enum';

export const PackagedGoodsControlManagement = () => {
  const toast = useRef<any>(undefined);

  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [packagedGoodsControls, setPackagedGoodsControls] = useState<PackagedGoodsControl[]>(null);
  const [packagedGoodsControlToDelete, setPackagedGoodsControlToDelete] = useState<PackagedGoodsControl>();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<PackagedGoodsControl>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const [addEditDialogReadOnly, setAddEditDialogReadOnly] = useState(false);
  const { t } = useTranslation();

  const showSaveToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('packagedGoodsControls.toast_saved'),
      life: TOAST_LIFETIME,
    });
  };

  const showAddToast = (summary: string) => {
    toast.current.show({
      severity: 'success',
      summary: summary,
      detail: t('packagedGoodsControls.toast_added'),
      life: TOAST_LIFETIME,
    });
  };

  const showDeleteToast = (summary: string) => {
    toast.current.show({
      severity: 'info',
      summary: summary,
      detail: t('packagedGoodsControls.toast_deleted'),
      life: TOAST_LIFETIME,
    });
  };

  const showErrorToast = (summary: string, detail: string) => {
    toast.current.show({
      severity: 'error',
      summary: summary,
      detail: detail,
      life: TOAST_LIFETIME,
    });
  };

  const controlTypes = useMemo(
    () => enumService.getTranslatedEnum(t, ControlType, 'packagedGoodsControls.controlTypes'),
    [t]
  );

  const statuses = useMemo(() => enumService.getTranslatedEnum(t, Status, 'packagedGoodsControls.statuses'), [t]);

  const results = useMemo(() => enumService.getTranslatedEnum(t, Result, 'packagedGoodsControls.results'), [t]);

  const columnDefinitions: ColumnDefinitions = useMemo(
    () => ({
      name: {
        type: 'string',
        translationKey: 'packagedGoodsControls.name',
      },
      controlType: {
        type: 'enum',
        translationKey: 'packagedGoodsControls.control_type',
        enumOptions: controlTypes,
        enumTranslationPrefix: 'packagedGoodsControls.controlTypes',
      },

      productName: {
        type: 'string',
        translationKey: 'packagedGoodsControls.product_name',
      },
      lotNumber: {
        type: 'string',
        translationKey: 'packagedGoodsControls.lot_number',
      },
      plannedDate: {
        type: 'date',
        translationKey: 'packagedGoodsControls.planned_date',
      },
      startDate: {
        type: 'date',
        translationKey: 'packagedGoodsControls.start_date',
      },
      endDate: {
        type: 'date',
        translationKey: 'packagedGoodsControls.end_date',
      },
      status: {
        type: 'enum',
        translationKey: 'packagedGoodsControls.status',
        enumOptions: statuses,
        enumTranslationPrefix: 'packagedGoodsControls.statuses',
      },
      result: {
        type: 'enum',
        translationKey: 'packagedGoodsControls.result',
        enumOptions: results,
        enumTranslationPrefix: 'packagedGoodsControls.results',
      },
      scales: {
        type: 'multiReference',
        translationKey: 'packagedGoodsControls.scale',
        filterable: false,
      },
    }),
    [controlTypes, statuses, results]
  );

  useEffect(() => {
    Object.keys(columnDefinitions).forEach((fieldName) => {
      FilterService.register(`custom_${fieldName}`, () => true);
    });
  }, [columnDefinitions]);

  const DEFAULT_FILTERS = useMemo(() => {
    return {
      global: { value: '', searchMode: SearchMode.CONTAINS, matchMode: FilterMatchMode.CONTAINS },
      ...generateFiltersFromColumns(columnDefinitions),
    };
  }, [columnDefinitions]);

  const {
    paginatorState,
    setPaginatorState,
    paginationParameters,
    setPaginationParameters,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination(false);

  const [filterState, setFilterState] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));
  const [filterRequest, setFilterRequest] = useState<TableFilterData>(structuredClone(DEFAULT_FILTERS));

  const initFilters = () => {
    setFilterState(structuredClone(DEFAULT_FILTERS));
    setFilterRequest(structuredClone(DEFAULT_FILTERS));
    setGlobalFilter('');
  };

  const clearFilter = () => {
    initFilters();
  };

  const loadData = async () => {
    setLoading(true);
    const packagedGoodsControlsResponse = await packagedGoodsControlsService.search(
      filterRequest,
      paginationParameters
    );

    const totalCount = packagedGoodsControlsResponse.totalCount;
    const totalPages =
      packagedGoodsControlsResponse.totalCount < 1 ? 1 : Math.ceil(totalCount / paginationParameters.page.size);
    setPaginatorState((prevStat) => {
      return {
        ...prevStat,
        totalPages: totalPages,
        currentPage: prevStat.currentPage > totalPages ? 1 : prevStat.currentPage,
      };
    });

    const data = packagedGoodsControlsResponse.data;
    setPackagedGoodsControls(data);
    setLoading(false);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
    // no filterRequest in dependency array, as filter change resets page to 1 and triggers data reload anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationParameters]);

  useEffect(() => {
    handlePageChange(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRequest]);

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setGlobalFilter(value);

    const filters1 = { ...filterState };
    (filters1['global'] as any).value = value;
    setFilterState(filters1);

    calculateFilterRequest(filters1);
  };

  const tableHeader = (
    <div className="table-header">
      {t('packagedGoodsControls.header')}
      <span>
        <Button
          icon="pi pi-filter-slash"
          className="p-button-outlined mr-2"
          label={t('common.clear')}
          onClick={clearFilter}
        />
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText value={globalFilter} onChange={onGlobalFilterChange} placeholder={t('common.search')} />
          <Button
            type="button"
            icon="pi pi-plus"
            label={t('common.add')}
            className="p-button ml-2"
            onClick={() => {
              addPackagedGoodsControl();
            }}
          ></Button>
        </span>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const confirmDeletePackagedGoodsControl = (packagedGoodsControl: PackagedGoodsControl) => {
    setPackagedGoodsControlToDelete(packagedGoodsControl);
    setDeleteDialogVisible(true);
  };

  const deletePackagedGoodsControl = async () => {
    setLoading(true);
    await packagedGoodsControlsService.delete(packagedGoodsControlToDelete.id);

    showDeleteToast(packagedGoodsControlToDelete.name);

    await loadData();

    setDeleteDialogVisible(false);
  };

  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const previewPackagedGoodsControl = (_packagedGoodsControl: PackagedGoodsControl) => {
    setSelectedRowToEdit(_packagedGoodsControl);
    setAddEditDialogReadOnly(true);
    setAddEditDialogVisible(true);
  };

  const editPackagedGoodsControl = (_packagedGoodsControl: PackagedGoodsControl) => {
    setSelectedRowToEdit(_packagedGoodsControl);
    setAddEditDialogReadOnly(false);
    setAddEditDialogVisible(true);
  };

  const addPackagedGoodsControl = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogReadOnly(false);
    setAddEditDialogVisible(true);
  };

  const execute = async (action: () => Promise<void>, summary: string) => {
    try {
      await action();
    } catch (error: any) {
      if (error.response.status === 409) {
        // TODO:: something more user friendly?
        showErrorToast(summary, error.response.data.error);
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadData();
  };

  const startPackagedGoodsControl = async (packagedGoodsControl: PackagedGoodsControl) => {
    await execute(() => packagedGoodsControlsService.start(packagedGoodsControl.id), packagedGoodsControl.name);
  };

  const resumePackagedGoodsControl = async (packagedGoodsControl: PackagedGoodsControl) => {
    await execute(() => packagedGoodsControlsService.resume(packagedGoodsControl.id), packagedGoodsControl.name);
  };

  const pausePackagedGoodsControl = async (packagedGoodsControl: PackagedGoodsControl) => {
    await execute(() => packagedGoodsControlsService.pause(packagedGoodsControl.id), packagedGoodsControl.name);
  };

  const completePackagedGoodsControl = async (packagedGoodsControl: PackagedGoodsControl) => {
    await execute(() => packagedGoodsControlsService.finish(packagedGoodsControl.id), packagedGoodsControl.name);
  };

  const cancelPackagedGoodsControl = async (packagedGoodsControl: PackagedGoodsControl) => {
    await execute(() => packagedGoodsControlsService.cancel(packagedGoodsControl.id), packagedGoodsControl.name);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <Button
        label={t('common.yes')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => deletePackagedGoodsControl()}
      />
    </>
  );

  const actionTemplate = (data: PackagedGoodsControl) => (
    <span>
      <Button
        type="button"
        className="p-button-success mr-2"
        icon="pi pi-eye"
        tooltip={t('common.preview')}
        tooltipOptions={{ position: 'top' }}
        onClick={() => {
          previewPackagedGoodsControl(data);
        }}
      />
      {data.status === Status.PLANNED && (
        <Button
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-pencil"
          tooltip={t('common.edit')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            editPackagedGoodsControl(data);
          }}
        />
      )}

      {data.status === Status.PLANNED && (
        <WriteOnlyButton
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-play"
          tooltip={t('common.start')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            startPackagedGoodsControl(data);
          }}
        />
      )}

      {data.status === Status.PAUSED && (
        <WriteOnlyButton
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-play"
          tooltip={t('common.resume')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            resumePackagedGoodsControl(data);
          }}
        />
      )}
      {data.status === Status.IN_PROGRESS && (
        <WriteOnlyButton
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-pause"
          tooltip={t('common.pause')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            pausePackagedGoodsControl(data);
          }}
        />
      )}
      {data.status === Status.IN_PROGRESS && data.controlType === ControlType.FULL && (
        <WriteOnlyButton
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-check"
          tooltip={t('common.complete')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            completePackagedGoodsControl(data);
          }}
        />
      )}

      {(data.status === Status.IN_PROGRESS || data.status === Status.PAUSED) && (
        <WriteOnlyButton
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-times"
          tooltip={t('common.cancel')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            cancelPackagedGoodsControl(data);
          }}
        />
      )}

      {data.status === Status.COMPLETED && (
        <>
          <Button
            type="button"
            className="p-button-success mr-2"
            icon="pi pi-file-pdf"
            tooltip={t('common.export_pdf')}
            tooltipOptions={{ position: 'top' }}
            onClick={async () => {
              await downloadReport(data, REPORT_TYPES.PDF);
            }} // TODO:: disabled after click?
          />
          <Button
            type="button"
            className="p-button-success mr-2"
            icon="pi pi-file-excel"
            tooltip={t('common.export_excel')}
            tooltipOptions={{ position: 'top' }}
            onClick={async () => {
              await downloadReport(data, REPORT_TYPES.EXCEL);
            }} // TODO:: disabled after click?
          />
        </>
      )}

      {data.status !== Status.IN_PROGRESS && (
        <WriteOnlyButton
          type="button"
          className="p-button-success mr-2"
          icon="pi pi-trash"
          tooltip={t('common.delete')}
          tooltipOptions={{ position: 'top' }}
          onClick={() => {
            confirmDeletePackagedGoodsControl(data);
          }}
        />
      )}
    </span>
  );

  const downloadReport = async (packagedGoodsControl: PackagedGoodsControl, reportFormat: REPORT_TYPES) => {
    let report: any = {
      download: true,
    };

    if (reportFormat === REPORT_TYPES.EXCEL) {
      report.format = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      report.extension = '.xlsx';
    } else {
      report.format = 'application/pdf';
      report.extension = '.pdf';
    }

    await exportReport(packagedGoodsControl, report);
  };

  const prepareFileName = (controlName: string): string => {
    const formattedDate = format(Date.now(), 'yyyyMMdd_HHmmss');
    return `${controlName} ${formattedDate}`;
  };

  const exportReport = async (packagedGoodsControl: PackagedGoodsControl, options: any) => {
    const response = await packagedGoodsControlsService.getReport(packagedGoodsControl.id, options.format);

    !options.download && getFileFromBuffer(response.data, options.format);
    options.download && getFileFromBuffer(response.data, options.format, prepareFileName(packagedGoodsControl.name));
  };

  const calculateFilterRequest = (data) => {
    const filters = structuredClone(data);

    setFilterRequest(filters);
  };

  const handleChangeFilter = (name: string, value: any, triggerDataRefresh = false) => {
    const newFilters = { ...filterState };
    if (Array.isArray(value) && value.filter((v) => v != null).length === 0) {
      value = [];
    }
    newFilters[name].value = value;

    setFilterState(newFilters);

    if (triggerDataRefresh) {
      calculateFilterRequest(newFilters);
    }
  };

  const handleFilterApplyClick = async () => {
    calculateFilterRequest(filterState);
  };

  const handleSort = (e: any) => {
    setPaginationParameters({
      ...paginationParameters,
      order: {
        isAscending: e.sortOrder === 1 ? true : false,
        orderColumn: e.sortField,
      },
    });
  };

  const { createColumns } = useColumnFactory(filterState, handleChangeFilter, handleFilterApplyClick, t);

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={packagedGoodsControls}
            paginator
            className="p-datatable-customers"
            rows={paginatorState.rows}
            dataKey="id"
            sortOrder={paginationParameters.order.isAscending ? 1 : -1}
            sortField={paginationParameters.order.orderColumn}
            onSort={handleSort}
            rowHover
            emptyMessage={t('packagedGoodsControls.empty_message')}
            loading={loading}
            header={tableHeader}
            filterDisplay="menu"
            filters={filterState}
            paginatorTemplate={getPaginatorTemplate(paginatorState, handlePageChange, handleRowsPerPageChange)}
          >
            {createColumns(columnDefinitions)}
            <Column
              headerStyle={{ width: '12rem' }}
              style={{ minWidth: '12rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
              frozen
              alignFrozen="right"
            ></Column>
          </DataTable>
          <PackagedGoodsControlDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            readOnly={addEditDialogReadOnly}
            packagedGoodsControl={selectedRowToEdit}
            showAddToast={showAddToast}
            showSaveToast={showSaveToast}
          />

          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {packagedGoodsControlToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: packagedGoodsControlToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};
